import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';
import { addLocalDevProject, getLocalDevUsers, isLocalDevCompanyId } from '../utils/localDevAuth';

const CreateProject = ({ user }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        start_date: '',
        end_date: '',
        manager_id: '',
        status: 'ongoing',
    });
    const [managers, setManagers] = useState([]);
    const [error, setError] = useState('');
    const companyId = user?.company?.id || localStorage.getItem('company_id');
    const isLocalDevSession = isLocalDevCompanyId(companyId);

    useEffect(() => {
        const fetchManagers = async () => {
            if (isLocalDevSession) {
                const companyManagers = getLocalDevUsers(companyId).filter(u => u.role === 'manager');
                setManagers(companyManagers);
                return;
            }

            try {
                const token = getStoredAccessToken();
                if (!token) {
                    handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
                    return;
                }
                const response = await axios.get(`${API_URL}/api/users/`, {
                    headers: getAuthHeaders(token),
                });

                const companyManagers = response.data.filter(u => {
                    const isManager = u.role === 'manager';
                    const isSameCompany = String(u.company?.id) === String(user.company?.id);
                    return isManager && isSameCompany;
                });

                setManagers(companyManagers);
            } catch (err) {
                console.error('Error fetching managers:', err);
                if (err.response?.status === 401) {
                    handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
                    return;
                }
                setError(getApiErrorMessage(err, {
                    fallback: 'Failed to load managers. Please try again.',
                }));
            }
        };

        fetchManagers();
    }, [companyId, isLocalDevSession, navigate, user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            if (isLocalDevSession) {
                addLocalDevProject({
                    ...formData,
                    companyId,
                });
                navigate('/admin?refresh=true');
                return;
            }

            const token = getStoredAccessToken();
            if (!token) {
                handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
                return;
            }
            const payload = {
                ...formData,
                manager_id: formData.manager_id ? Number(formData.manager_id) : '',
            };

            await axios.post(`${API_URL}/api/projects/`, payload, {
                headers: getAuthHeaders(token),
            });
            navigate('/admin');
        } catch (err) {
            console.error('Error creating project:', err);
            if (err.response?.status === 401) {
                handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
                return;
            }
            setError(getApiErrorMessage(err, {
                fallback: 'Failed to create project. Please try again.',
            }));
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4 font-sans">
            <div className="card w-full max-w-xs p-6 sm:max-w-sm sm:p-8 md:max-w-md">
                <h2 className="section-title mb-4 text-center text-xl sm:mb-6 sm:text-2xl">Create Project</h2>
                {error && <div className="alert-error mb-4 text-center">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-text font-medium mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="name">Project Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                            required
                        />
                    </div>
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-text font-medium mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="start_date">Start Date</label>
                        <input
                            type="date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleInputChange}
                            className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                            required
                        />
                    </div>
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-text font-medium mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="end_date">End Date</label>
                        <input
                            type="date"
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleInputChange}
                            className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                            required
                        />
                    </div>
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-text font-medium mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="manager_id">Assigned to (Manager)</label>
                        <select
                            name="manager_id"
                            value={formData.manager_id}
                            onChange={handleInputChange}
                            className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                            required
                        >
                            <option value="">Select a manager</option>
                            {managers.map(manager => (
                                <option key={manager.id} value={manager.id}>
                                    {manager.email}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-text font-medium mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="status">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                            required
                        >
                            <option value="ongoing">Ongoing</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        className="primary-btn w-full p-2 sm:p-3 text-sm sm:text-base"
                    >
                        Create Project
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreateProject;
